import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import {
  UserPlus,
  KeyRound,
  Building2,
  Mail,
  MoreHorizontal,
  Users,
  UserCheck,
  UserX,
  Ban,
  Send,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getApiErrorMessage } from '@/api/apiErrors';
import type { AdminUser, Environment, Invite } from '@/api/apiTypes';
import { ActionDropdownMenu, type DropdownActionItem } from '@/components/common/ActionDropdownMenu';
import { PageIntroHeader } from '@/components/common/PageIntroHeader';
import { SectionHeader } from '@/components/common/SectionHeader';
import { SectionSearchField } from '@/components/common/SectionSearchField';
import { SummaryTiles, type SummaryTileItem } from '@/components/common/SummaryTiles';
import { AppDataGrid } from '@/components/grid/AppDataGrid';
import { AdminInviteForm } from '@/components/users/AdminInviteForm';
import { PasswordResetModal } from '@/components/users/PasswordResetModal';
import { useEnvironments } from '@/hooks/useEnvironments';
import {
  useCancelInvite,
  useCreateInvite,
  useDeactivateAdminUser,
  useInvites,
  useReactivateAdminUser,
  useResendInvite,
  useAdminUsers,
} from '@/hooks/useAdminAccess';
import '@/pages/users/users.scss';

const EMPTY_USERS: AdminUser[] = [];
const EMPTY_INVITES: Invite[] = [];
const EMPTY_ENVIRONMENTS: Environment[] = [];

type CombinedAccessRow = {
  id: string;
  rowType: 'user' | 'invite';
  name: string;
  email: string;
  status: string;
  createdAt: string;
  userId?: number;
  isActiveUser?: boolean;
  inviteId?: number;
};

type ActionMenuState = {
  rowId: string;
  x: number;
  y: number;
};

const ACTION_MENU_WIDTH = 224;

function getInviteStatusLabel(status: Invite['status'], t: (key: string) => string) {
  if (status === 'pending') {
    return t('users.invites.pending');
  }

  if (status === 'accepted') {
    return t('users.invites.accepted');
  }

  return t('users.invites.cancelled');
}

function formatDisplayName(firstName: string, lastName: string, email: string) {
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || email;
}

function formatDateTime(timestamp: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp));
}

export function UsersPage() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteFormOpen, setInviteFormOpen] = useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState<ActionMenuState | null>(null);
  const usersQuery = useAdminUsers();
  const invitesQuery = useInvites('pending');
  const environmentsQuery = useEnvironments();
  const createInviteMutation = useCreateInvite();
  const resendInviteMutation = useResendInvite();
  const cancelInviteMutation = useCancelInvite();
  const deactivateUserMutation = useDeactivateAdminUser();
  const reactivateUserMutation = useReactivateAdminUser();

  const users = usersQuery.data ?? EMPTY_USERS;
  const invites = invitesQuery.data ?? EMPTY_INVITES;
  const environments = environmentsQuery.data ?? EMPTY_ENVIRONMENTS;

  const combinedRows = useMemo<CombinedAccessRow[]>(() => {
    const userRows: CombinedAccessRow[] = users.map((user) => ({
      id: `user-${user.id}`,
      rowType: 'user',
      name: formatDisplayName(user.first_name, user.last_name, user.email),
      email: user.email,
      status: user.is_active ? t('users.status.active') : t('users.status.inactive'),
      createdAt: user.created_at,
      userId: user.id,
      isActiveUser: user.is_active,
    }));

    const inviteRows: CombinedAccessRow[] = invites.map((invite) => ({
      id: `invite-${invite.id}`,
      rowType: 'invite',
      name: invite.email,
      email: invite.email,
      status: getInviteStatusLabel(invite.status, t),
      createdAt: invite.created_at,
      inviteId: invite.id,
    }));

    return [...userRows, ...inviteRows].sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    );
  }, [invites, t, users]);

  const filteredRows = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    if (!normalizedTerm) {
      return combinedRows;
    }

    return combinedRows.filter((row) => {
      const haystack = [row.name, row.email, row.status]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedTerm);
    });
  }, [combinedRows, searchTerm]);

  const summaryCards = useMemo<SummaryTileItem[]>(
    () => [
      {
        id: 'users',
        label: t('users.summary.users'),
        value: String(users.length),
        icon: Users,
        tone: 'blue',
      },
      {
        id: 'invites',
        label: t('users.summary.pendingInvites'),
        value: String(invites.length),
        icon: Mail,
        tone: 'amber',
      },
      {
        id: 'environments',
        label: t('users.summary.environments'),
        value: String(environments.length),
        icon: Building2,
        tone: 'green',
      },
    ],
    [environments.length, invites.length, t, users.length],
  );

  const selectedActionRow = actionMenu ? combinedRows.find((row) => row.id === actionMenu.rowId) ?? null : null;

  useEffect(() => {
    if (!actionMenu) {
      return undefined;
    }

    const closeMenu = () => setActionMenu(null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    window.addEventListener('click', closeMenu);
    window.addEventListener('contextmenu', closeMenu);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('click', closeMenu);
      window.removeEventListener('contextmenu', closeMenu);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [actionMenu]);

  const handleCreateInvite = useCallback(
    async (values: { email: string; environment_ids?: number[] }) => {
      try {
        await createInviteMutation.mutateAsync(values);
        toast.success(t('users.messages.inviteSuccess'));
        setInviteFormOpen(false);
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('users.messages.inviteError')));
      }
    },
    [createInviteMutation, t],
  );

  const handleResendInvite = useCallback(
    async (inviteId: number) => {
      try {
        await resendInviteMutation.mutateAsync(inviteId);
        toast.success(t('users.messages.resendSuccess'));
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('users.messages.resendError')));
      }
    },
    [resendInviteMutation, t],
  );

  const handleCancelInvite = useCallback(
    async (inviteId: number) => {
      try {
        await cancelInviteMutation.mutateAsync(inviteId);
        toast.success(t('users.messages.cancelSuccess'));
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('users.messages.cancelError')));
      }
    },
    [cancelInviteMutation, t],
  );

  const handleDeactivateUser = useCallback(
    async (userId: number) => {
      try {
        await deactivateUserMutation.mutateAsync(userId);
        toast.success(t('users.messages.deactivateSuccess'));
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('users.messages.deactivateError')));
      }
    },
    [deactivateUserMutation, t],
  );

  const handleReactivateUser = useCallback(
    async (userId: number) => {
      try {
        await reactivateUserMutation.mutateAsync(userId);
        toast.success(t('users.messages.reactivateSuccess'));
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('users.messages.reactivateError')));
      }
    },
    [reactivateUserMutation, t],
  );

  const selectedActionItems = useMemo<DropdownActionItem[]>(() => {
    if (!selectedActionRow) {
      return [];
    }

    if (selectedActionRow.rowType === 'user' && selectedActionRow.userId) {
      const userId = selectedActionRow.userId;

      return selectedActionRow.isActiveUser
        ? [
            {
              id: 'password-reset-user',
              label: t('users.actions.passwordReset'),
              icon: KeyRound,
              onSelect: () => setPasswordResetOpen(true),
            },
            {
              id: 'deactivate-user',
              label: t('users.actions.deactivateUser'),
              icon: UserX,
              tone: 'danger',
              disabled: deactivateUserMutation.isPending,
              onSelect: () => void handleDeactivateUser(userId),
            },
          ]
        : [
            {
              id: 'password-reset-user',
              label: t('users.actions.passwordReset'),
              icon: KeyRound,
              onSelect: () => setPasswordResetOpen(true),
            },
            {
              id: 'reactivate-user',
              label: t('users.actions.reactivateUser'),
              icon: UserCheck,
              disabled: reactivateUserMutation.isPending,
              onSelect: () => void handleReactivateUser(userId),
            },
          ];
    }

    if (selectedActionRow.rowType !== 'invite' || !selectedActionRow.inviteId) {
      return [];
    }

    const inviteId = selectedActionRow.inviteId;

    return [
      {
        id: 'resend-invite',
        label: t('users.actions.resendInvite'),
        icon: Send,
        disabled: resendInviteMutation.isPending,
        onSelect: () => void handleResendInvite(inviteId),
      },
      {
        id: 'cancel-invite',
        label: t('users.actions.cancelInvite'),
        icon: Ban,
        tone: 'danger',
        disabled: cancelInviteMutation.isPending,
        onSelect: () => void handleCancelInvite(inviteId),
      },
    ];
  }, [
    cancelInviteMutation.isPending,
    deactivateUserMutation.isPending,
    handleCancelInvite,
    handleDeactivateUser,
    handleReactivateUser,
    handleResendInvite,
    reactivateUserMutation.isPending,
    resendInviteMutation.isPending,
    selectedActionRow,
    t,
  ]);

  const combinedColumns = useMemo<ColDef<CombinedAccessRow>[]>(
    () => [
      {
        headerName: t('users.columns.name'),
        field: 'name',
        flex: 2,
        minWidth: 220,
      },
      {
        headerName: t('users.columns.email'),
        field: 'email',
        flex: 2,
        minWidth: 240,
      },
      {
        headerName: t('users.columns.status'),
        field: 'status',
        flex: 1,
        cellRenderer: ({ data }: { data?: CombinedAccessRow }) => {
          if (!data) {
            return null;
          }

          const variantClassName =
            data.status === t('users.status.active')
              ? 'status-pill--active'
              : data.status === t('users.invites.pending')
                ? 'environment-pill'
                : 'status-pill--inactive';

          if (variantClassName === 'environment-pill') {
            return <span className="environment-pill">{data.status}</span>;
          }

          return <span className={`status-pill ${variantClassName}`}>{data.status}</span>;
        },
        minWidth: 160,
      },
      {
        headerName: t('users.columns.createdAt'),
        field: 'createdAt',
        flex: 2,
        valueFormatter: ({ value }) => (typeof value === 'string' ? formatDateTime(value) : ''),
        minWidth: 190,
      },
      {
        headerName: t('users.columns.actions'),
        sortable: false,
        filter: false,
        floatingFilter: false,
        flex: 1,
        minWidth: 120,
        cellRenderer: ({ data }: { data?: CombinedAccessRow }) => {
          if (!data) {
            return null;
          }

          return (
            <div className="users-actions-cell">
              <button
                type="button"
                className="users-actions-cell__trigger"
                aria-haspopup="menu"
                aria-expanded={actionMenu?.rowId === data.id}
                aria-label={t('users.actions.moreActions')}
                onClick={(event) => {
                  event.stopPropagation();

                  const rect = event.currentTarget.getBoundingClientRect();
                  const x = Math.max(16, Math.min(rect.left, window.innerWidth - ACTION_MENU_WIDTH - 16));

                  setActionMenu((currentMenu) =>
                    currentMenu?.rowId === data.id
                      ? null
                      : {
                          rowId: data.id,
                          x,
                          y: rect.bottom + 8,
                        },
                  );
                }}
              >
                <MoreHorizontal size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
          );
        },
      },
    ],
    [actionMenu, t],
  );

  return (
    <div className="page-stack">
      <PageIntroHeader
        title={t('users.title')}
        description={t('users.description')}
        className="users-page__intro"
        copyClassName="users-page__intro-copy"
        actionsClassName="users-hero-actions"
        actions={
          <>
          <button
            type="button"
            className="button button--secondary dashboard-export-btn users-hero-action-btn"
            onClick={() => setInviteFormOpen(true)}
          >
            <UserPlus size={16} />
            {t('users.actions.create')}
          </button>
          </>
        }
      />

      <SummaryTiles items={summaryCards} className="users-summary-grid" cardClassName="dashboard-summary-card users-summary-card" />

      <article className="surface-card">
        <SectionHeader
          icon={Users}
          title={t('users.tables.unifiedTitle')}
          description={t('users.tables.unifiedDescription')}
        />

        <SectionSearchField
          value={searchTerm}
          placeholder={t('users.filters.searchPlaceholder')}
          ariaLabel={t('users.filters.searchLabel')}
          onChange={setSearchTerm}
          filtersClassName="users-search-card__filters"
          fieldClassName="users-search-field"
        />

        {usersQuery.isError ? <p>{getApiErrorMessage(usersQuery.error, t('users.messages.loadUsersError'))}</p> : null}
        {environmentsQuery.isError ? (
          <p>{getApiErrorMessage(environmentsQuery.error, t('users.messages.loadEnvironmentsError'))}</p>
        ) : null}
        {usersQuery.isPending ? <p>{t('users.messages.loadingUsers')}</p> : null}
        {invitesQuery.isError ? <p>{getApiErrorMessage(invitesQuery.error, t('users.messages.loadInvitesError'))}</p> : null}
        {usersQuery.isPending || invitesQuery.isPending ? <p>{t('users.messages.loadingTable')}</p> : null}

        {!usersQuery.isPending && !invitesQuery.isPending && !usersQuery.isError && !invitesQuery.isError ? (
          <AppDataGrid rowData={filteredRows} columnDefs={combinedColumns} height={420} pageSize={10} />
        ) : null}

        {!usersQuery.isPending && !invitesQuery.isPending && !usersQuery.isError && !invitesQuery.isError && filteredRows.length === 0 ? (
          <p>{t('users.messages.emptyTable')}</p>
        ) : null}
      </article>

      <AdminInviteForm
        open={inviteFormOpen}
        environments={environments}
        submitting={createInviteMutation.isPending}
        onOpenChange={setInviteFormOpen}
        onSubmit={handleCreateInvite}
      />

      <PasswordResetModal open={passwordResetOpen} onOpenChange={setPasswordResetOpen} />
      <ActionDropdownMenu
        open={Boolean(actionMenu && selectedActionItems.length)}
        actions={selectedActionItems}
        x={actionMenu?.x ?? 0}
        y={actionMenu?.y ?? 0}
        onClose={() => setActionMenu(null)}
        className="users-action-menu"
        itemClassName="users-action-menu__item"
        dangerItemClassName="users-action-menu__item--danger"
        iconClassName="users-action-menu__icon"
      />
    </div>
  );
}
